import clsx from 'clsx'
import { useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'

type BannerGuruWithoutDefinitiveKSProps = {
  hasPltKS?: boolean
  isAlsoPltKSInTheSameSchool?: boolean
  isPelaksanaanPltKSAndGuruSameSchoolEnabled?: boolean
  schoolName?: string
}

export function BannerGuruWithoutDefinitiveKS({
  hasPltKS = true,
  isAlsoPltKSInTheSameSchool = false,
  isPelaksanaanPltKSAndGuruSameSchoolEnabled = false,
  schoolName,
}: BannerGuruWithoutDefinitiveKSProps) {
  const [isDialogOpen, setDialogOpen] = useState(false)

  let title = 'Data atasan Anda tidak ditemukan'
  if (hasPltKS) {
    if (isAlsoPltKSInTheSameSchool) {
      if (isPelaksanaanPltKSAndGuruSameSchoolEnabled) {
        title =
          'Anda akan dinilai oleh Tim Kerja (Pengawas Sekolah) dan Dinas Pendidikan'
      } else {
        title = 'Penting! Pengelolaan Kinerja tertunda'
      }
    } else {
      title = 'Atasan Anda berstatus Plt. (Pelaksana Tugas)'
    }
  }

  const isCritical =
    !hasPltKS ||
    (isAlsoPltKSInTheSameSchool && !isPelaksanaanPltKSAndGuruSameSchoolEnabled)

  return (
    <>
      <article
        className={clsx(
          'rounded p-5 border flex space-x-4',
          isCritical ? 'border-red-50 bg-red-5' : 'border-blue-70 bg-blue-5'
        )}
        role="alert"
      >
        <Icon color={isCritical ? 'critical' : 'informational'} fontSize="lg">
          {isCritical ? 'warning' : 'info'}
        </Icon>
        <div className="space-y-1">
          <Text variant="heading-lg" as="h3">
            {title}
          </Text>
          <Content
            hasPltKS={hasPltKS}
            isAlsoPltKSInTheSameSchool={isAlsoPltKSInTheSameSchool}
            isPelaksanaanPltKSAndGuruSameSchoolEnabled={
              isPelaksanaanPltKSAndGuruSameSchoolEnabled
            }
            schoolName={schoolName}
          />
          {!isAlsoPltKSInTheSameSchool && (
            <button
              type="button"
              className="text-blue-70 !mt-4"
              onClick={() => setDialogOpen(true)}
            >
              <Text as="span" variant="action" color="inherit">
                Apa yang dapat saya lakukan?
              </Text>
            </button>
          )}
        </div>
      </article>

      <Dialog
        isOpen={isDialogOpen}
        size="md"
        onClose={() => setDialogOpen(false)}
      >
        <DialogOverlay />
        <DialogContent>
          <DialogHeader
            borderless
            title={`${title}, berikut yang Anda dapat lakukan:`}
          />
          <div className="pt-6 px-6 space-y-4">
            <ul className="space-y-4">
              <li className="flex space-x-3">
                <div className="w-6 h-6 rounded-full bg-gray-80 flex justify-center items-center flex-shrink-0">
                  <Text variant="body-sm" color="inverse">
                    1
                  </Text>
                </div>
                <div className="space-y-1">
                  <Text variant="action">
                    {hasPltKS
                      ? 'Bantu kami konfirmasi ke atasan Anda'
                      : 'Bantu kami konfirmasi data'}
                  </Text>
                  <Text color="subdued">
                    {hasPltKS
                      ? 'Kabari atasan Anda untuk konfirmasi data agar Anda dapat melanjutkan Pengelolaan Kinerja.'
                      : 'Mohon hubungi operator sekolah atau Dinas Pendidikan setempat untuk menyesuaikan data di Dapodik.'}
                  </Text>
                </div>
              </li>
              <li className="flex space-x-3">
                <div className="w-6 h-6 rounded-full bg-gray-80 flex justify-center items-center flex-shrink-0">
                  <Text variant="body-sm" color="inverse">
                    2
                  </Text>
                </div>
                <div className="space-y-1">
                  <Text variant="action">
                    {hasPltKS
                      ? 'Lanjut ke tahap berikutnya setelah atasan sudah konfirmasi'
                      : 'Lanjut ke tahap berikutnya setelah data atasan sudah tersedia'}
                  </Text>
                  <Text color="subdued">
                    Untuk saat ini, isi perencanaan Anda sudah kami simpan.
                  </Text>
                </div>
              </li>
            </ul>

            <div className="flex space-x-4 p-3 items-center justify-between bg-surface-informational-subdued rounded">
              <Text>Kunjungi Pusat Bantuan untuk info selengkapnya.</Text>
              <Button
                color="blue"
                variant="ghost"
                as="a"
                href={PANDUAN_PENGELOLAAN_URL}
                target="_blank"
                rel="noreferrer"
              >
                Ke Pusat Bantuan
              </Button>
            </div>
          </div>
          <DialogFooter borderless className="mb-2">
            <Button
              color="blue"
              size="lg"
              className="mx-auto"
              onClick={() => setDialogOpen(false)}
            >
              Saya mengerti
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

type ContentProps = BannerGuruWithoutDefinitiveKSProps

function Content({
  hasPltKS,
  isAlsoPltKSInTheSameSchool,
  isPelaksanaanPltKSAndGuruSameSchoolEnabled,
  schoolName,
}: ContentProps) {
  if (hasPltKS) {
    if (isAlsoPltKSInTheSameSchool) {
      return isPelaksanaanPltKSAndGuruSameSchoolEnabled ? (
        <Text color="subdued">
          Hal ini disebabkan Anda menjabat sebagai Guru dan Plt. Kepala Sekolah
          di satuan pendidikan yang sama{schoolName ? `: ${schoolName}` : ''}.
          Jika ada kesalahan, mohon kunjungi Pusat Bantuan.
        </Text>
      ) : (
        <Text color="subdued">
          Untuk guru yang menjabat sebagai Plt. Kepala Sekolah di sekolah yang
          sama, tahap Pelaksanaan baru dapat dilakukan mulai dari
          <strong> Mei</strong>.
        </Text>
      )
    }

    return (
      <Text color="subdued">
        Mohon kabari atasan Anda untuk konfirmasi data agar Anda dapat
        melanjutkan Pengelolaan Kinerja.
      </Text>
    )
  }

  return (
    <Text color="subdued">
      Saat ini kami tidak bisa menemukan data atasan Anda. Mohon hubungi
      operator sekolah atau Dinas Pendidikan setempat untuk menyesuaikan data di
      Dapodik. Anda dapat lanjut ke Pelaksanaan Kinerja saat data atasan Anda
      sudah tersedia.
    </Text>
  )
}
